import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Save, ShieldAlert } from "lucide-react";

// Minimal, admin-only JSON editor for workflow definitions. A full visual
// builder can come later — this is the "add workflow content without
// changing application code" surface the spec requires, and every save
// creates a new version. Running instances are unaffected (they hold their
// own snapshot).
export default function PracticeOpsAdmin() {
  const { user, loading } = useAuth();
  const [defs, setDefs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/practice-ops/definitions/all");
      setDefs(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load definitions");
    }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
  }, [user]);

  const openDef = (d) => {
    setSelected(d.slug);
    const editable = {
      name: d.name,
      description: d.description || "",
      category: d.category || "",
      icon: d.icon || "",
      questions: d.questions || [],
      stages: d.stages || [],
      tasks: d.tasks || [],
      active: d.active !== false,
    };
    setDraft(JSON.stringify(editable, null, 2));
    setParseError("");
  };

  const save = async () => {
    if (!selected) return;
    let payload;
    try {
      payload = JSON.parse(draft);
    } catch (e) {
      setParseError("Invalid JSON: " + e.message);
      return;
    }
    setParseError("");
    setBusy(true);
    try {
      await api.put(`/practice-ops/definitions/${selected}`, payload);
      toast.success("Saved new version");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save definition");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (user?.role !== "admin") return <Navigate to="/practice-ops" replace />;

  const activeDefs = (defs || []).filter((d) => d.active);
  const inactiveDefs = (defs || []).filter((d) => !d.active);

  return (
    <Layout wide>
      <div className="mb-6">
        <Link to="/practice-ops" data-testid="po-admin-back"
          className="inline-flex items-center gap-1.5 text-sm text-brand-secondary hover:text-brand-ink transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Back to Practice Ops
        </Link>
      </div>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1 flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> Admin only
        </div>
        <h1 className="font-serif text-4xl text-brand-ink">Workflow definitions</h1>
        <p className="text-brand-body text-sm mt-2 max-w-2xl">
          Add or edit workflow content without changing application code. Saving creates a new
          version — active workflows already running keep their existing snapshot and are not
          affected.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px,1fr]">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-2">Active</div>
          <div className="rounded-2xl border border-brand-linen bg-white divide-y divide-brand-linen">
            {activeDefs.length === 0 ? (
              <div className="p-4 text-brand-muted text-sm">None.</div>
            ) : activeDefs.map((d) => (
              <button
                key={d.slug + "-v" + d.version}
                onClick={() => openDef(d)}
                data-testid={`po-admin-def-${d.slug}`}
                className={`w-full text-left px-4 py-3 hover:bg-brand-linen/30 transition-colors ${selected === d.slug ? "bg-brand-linen/40" : ""}`}
              >
                <div className="text-brand-ink text-sm font-medium truncate">{d.name}</div>
                <div className="text-brand-muted text-xs mt-0.5">v{d.version} · {d.category || "—"}</div>
              </button>
            ))}
          </div>

          {inactiveDefs.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-widest text-brand-muted mt-6 mb-2">Older versions</div>
              <div className="rounded-2xl border border-brand-linen bg-white divide-y divide-brand-linen">
                {inactiveDefs.map((d) => (
                  <div key={d.slug + "-v" + d.version} className="px-4 py-3 text-brand-muted text-sm">
                    <div className="truncate">{d.name}</div>
                    <div className="text-xs mt-0.5">v{d.version} · inactive</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          {!selected ? (
            <div className="rounded-2xl border border-dashed border-brand-linen bg-brand-linen/20 p-10 text-center text-brand-muted text-sm">
              Select a definition on the left to edit.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-brand-secondary text-sm">
                  Editing <span className="font-mono">{selected}</span>
                </div>
                <button
                  onClick={save}
                  disabled={busy}
                  data-testid="po-admin-save"
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-brand-bone px-4 py-2 text-sm hover:bg-brand-inkHover transition-colors disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {busy ? "Saving…" : "Save as new version"}
                </button>
              </div>
              {parseError && (
                <div data-testid="po-admin-parse-error" className="mb-3 rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700 font-mono">
                  {parseError}
                </div>
              )}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="po-admin-json-editor"
                spellCheck={false}
                className="w-full h-[600px] rounded-2xl border border-brand-linen bg-white p-4 text-xs font-mono focus:outline-none focus:border-brand-ink/40"
              />
              <div className="mt-2 text-[11px] text-brand-muted">
                Reminder: never include patient-identifiable or clinical information in question labels, task titles or guidance.
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
